import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorMessage from '../forms/formComponents/ErrorMessage';
import SubmitButton from '../forms/formComponents/SubmitButton';
import RSVPControls from '../forms/teamForms/events/RSVPControls';
import { useJoin } from './joinContext';
import { db, functions } from '../../firebase-client';
import {
  buildLearnToRowEventDocId,
  calculateLearnToRowCheckoutPreview,
  normalizeLearnToRowSignupMode,
} from '../teams/utils/learnToRow';
import { getDocumentActionLabels, getDocumentSourceUrl, triggerDocumentDownload } from '../teams/utils/documentUtils';
import { loadPublicLearnToRowCourses } from './learnToRowCourseQueries';
import { coursePaymentSetupMessage, isCoursePaymentSetupComplete } from './courseSignupSetup';
import { CHOOSE_OPTION_LABEL, getFormFieldPlaceholder } from '../../utils/formFieldPresentation';
import CourseBoatSummary from './CourseBoatSummary';
import { buildChildAccountSetupPath } from '../../utils/childAccountReturn';

const FIRESTORE_READ_TIMEOUT_MS = 6000;
const CHECKOUT_PARTICIPANT_POLL_MS = 1000;
const CHECKOUT_PARTICIPANT_MAX_ATTEMPTS = 45;
const CHECKOUT_INTENT_POLL_MS = 1000;
const CHECKOUT_INTENT_MAX_ATTEMPTS = 45;
const CHECKOUT_FAILURE_STATUSES = new Set(['expired', 'payment_failed']);

function withTimeout(promise, timeoutMs = FIRESTORE_READ_TIMEOUT_MS, fallback = null) {
  let timeoutId;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
    }),
  ]).finally(() => clearTimeout(timeoutId));
}

function normalizeSessions(sessions = []) {
  return (Array.isArray(sessions) ? sessions : [])
    .filter((session) => session?.date && session?.startTime && session?.endTime)
    .sort((left, right) => `${left.date}T${left.startTime}`.localeCompare(`${right.date}T${right.startTime}`));
}

function formatMoney(minor = 0, currency = 'GBP') {
  const amount = Number(minor || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'GBP').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${String(currency || 'GBP').toUpperCase()}`;
  }
}

function formatSession(session = {}) {
  const start = new Date(`${session.date}T${session.startTime}`);
  const end = new Date(`${session.date}T${session.endTime}`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Session date/time incomplete';
  return `${start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · ${start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}-${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatSessionParts(session = {}) {
  const start = new Date(`${session.date}T${session.startTime}`);
  const end = new Date(`${session.date}T${session.endTime}`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return {
      dayLabel: 'Date incomplete',
      dateLabel: '',
      timeLabel: 'Time incomplete',
    };
  }
  return {
    dayLabel: start.toLocaleDateString('en-GB', { weekday: 'long' }),
    dateLabel: start.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    timeLabel: `${start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
  };
}

function normalizeParticipantStatus(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeCheckoutIntentStatus(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeCourseSnapshot(value = {}, fallbackId = '') {
  const course = value && typeof value === 'object' ? value : {};
  const id = typeof course?.id === 'string' && course.id.trim() ? course.id.trim() : fallbackId;
  if (!id) return null;
  return {
    id,
    name: typeof course?.name === 'string' ? course.name.trim() : '',
    description: typeof course?.description === 'string' ? course.description.trim() : '',
    sessions: normalizeSessions(course?.sessions || []),
    requireSessionRsvp: course?.requireSessionRsvp !== false,
    courseFeeMinor: Math.max(0, Math.round(Number(course?.courseFeeMinor || 0))),
    courseFeeMajor: typeof course?.courseFeeMajor === 'string' ? course.courseFeeMajor.trim() : '',
    memberNestFeeHandling: course?.memberNestFeeHandling === 'deduct' ? 'deduct' : (course?.memberNestFeeHandling === 'add' ? 'add' : ''),
    stripeFeeHandling: course?.stripeFeeHandling === 'deduct' ? 'deduct' : (course?.stripeFeeHandling === 'add' ? 'add' : ''),
    capacity: Math.max(1, Number(course?.capacity || 1)),
  };
}

function upsertCourse(courses = [], course = null) {
  if (!course?.id) return courses;
  const withoutCourse = courses.filter((entry) => entry?.id !== course.id);
  return [course, ...withoutCourse]
    .filter((entry, index, entries) => entries.findIndex((candidate) => candidate.id === entry.id) === index)
    .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
}

function normalizeRsvpStatus(value) {
  return value === 'yes' || value === 'no' ? value : '';
}

function formatRsvpStatusLabel(value) {
  const normalized = normalizeRsvpStatus(value);
  if (normalized === 'yes') return 'RSVP: Yes';
  if (normalized === 'no') return 'RSVP: No';
  return 'Not answered yet';
}

function LearnToRowSessionRsvpCard({
  session,
  sessionIndex,
  selectedCourseId,
  teamId,
  targetUserId,
  onStatusChange,
}) {
  const eventId = session?.eventId || buildLearnToRowEventDocId(selectedCourseId, session?.id);
  const [myRsvpStatus, setMyRsvpStatus] = useState('');
  const [rsvpBusy, setRsvpBusy] = useState(false);

  useEffect(() => {
    if (!eventId || !targetUserId || !teamId) {
      setMyRsvpStatus('');
      onStatusChange?.(session?.id, '');
      return undefined;
    }
    const rsvpRef = doc(db, 'teams', teamId, 'events', eventId, 'rsvps', targetUserId);
    let cancelled = false;
    const applySnapshot = (snapshot) => {
      if (cancelled) return;
      const nextStatus = normalizeRsvpStatus(snapshot?.data?.()?.status);
      setMyRsvpStatus((current) => (current === nextStatus ? current : nextStatus));
      onStatusChange?.(session?.id, nextStatus);
    };
    const unsubscribe = safeOnSnapshot(
      rsvpRef,
      applySnapshot,
      () => {
        withTimeout(getDoc(rsvpRef).catch(() => null), FIRESTORE_READ_TIMEOUT_MS, null)
          .then((snapshot) => {
            if (snapshot?.exists?.()) applySnapshot(snapshot);
          });
      }
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [eventId, onStatusChange, session?.id, sessionIndex, targetUserId, teamId]);

  const handleSessionRsvp = useCallback(async (status, options = {}) => {
    const normalized = normalizeRsvpStatus(status);
    if (normalized) {
      setMyRsvpStatus((current) => (current === normalized ? current : normalized));
      onStatusChange?.(session?.id, normalized);
    }
    try {
      setRsvpBusy(true);
      const setEventRsvp = httpsCallable(functions, 'setEventRsvp');
      await setEventRsvp({
        teamId,
        eventId,
        targetUserId,
        status,
        scope: options.scope || 'series',
      });
    } catch (err) {
      if (normalized) {
        setMyRsvpStatus(myRsvpStatus);
        onStatusChange?.(session?.id, normalizeRsvpStatus(myRsvpStatus));
      }
      alert(err?.message || 'Sorry, something went wrong saving your RSVP.');
    } finally {
      setRsvpBusy(false);
    }
  }, [eventId, myRsvpStatus, onStatusChange, session?.id, targetUserId, teamId]);

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontWeight: 600 }}>Session {sessionIndex + 1}</div>
      <div className="text-muted" style={{ marginTop: 4 }}>{formatSession(session)}</div>
      <CourseBoatSummary teamId={teamId} courseType="learn_to_row" courseId={selectedCourseId} sessionId={session.id} />
      <div style={{ marginTop: 12 }}>
        <RSVPControls
          enabled={Boolean(eventId && targetUserId && teamId)}
          isFull={false}
          myRsvpStatus={normalizeRsvpStatus(myRsvpStatus)}
          myOccurrenceRsvpStatus={null}
          mySeriesRsvpStatus={normalizeRsvpStatus(myRsvpStatus)}
          isRecurring={false}
          occurrenceId={null}
          setRsvpStatus={handleSessionRsvp}
          rsvpBusy={rsvpBusy}
          capacityNum={null}
          currentYesCount={0}
          title="Session RSVP"
        />
      </div>
      <div className="text-muted" style={{ marginTop: 10, fontSize: '0.95rem', fontWeight: myRsvpStatus ? 600 : 400 }}>
        {rsvpBusy ? 'Saving RSVP…' : formatRsvpStatusLabel(myRsvpStatus)}
      </div>
    </div>
  );
}

function normalizeQuestionOptions(options = []) {
  return Array.isArray(options)
    ? options.map((option) => String(option || '').trim()).filter(Boolean)
    : [];
}

function normalizeLearnToRowQuestion(question = {}) {
  const type = ['text', 'textarea', 'number', 'yesno', 'date', 'select', 'checkbox'].includes(question?.type)
    ? question.type
    : 'text';
  return {
    id: typeof question?.id === 'string' ? question.id.trim() : '',
    label: typeof question?.label === 'string' ? question.label.trim() : '',
    type,
    required: type === 'checkbox' ? true : question?.required === true,
    syncToProfile: question?.syncToProfile === true,
    options: type === 'select' ? normalizeQuestionOptions(question?.options) : [],
  };
}

function isPricingQuestion(question = {}) {
  return question?.id === 'pricingTag' || question?.id === 'pricingTags';
}

function normalizeLearnToRowQuestions(questions = []) {
  return (Array.isArray(questions) ? questions : [])
    .map((question) => normalizeLearnToRowQuestion(question))
    .filter((question) => question.id && question.label && !isPricingQuestion(question));
}

function normalizeJoinMode(value) {
  return ['club_join_form', 'club_join_form_plus_l2r', 'l2r_only'].includes(value)
    ? value
    : 'club_join_form';
}

function normalizeQuestionValueForStorage(question, value) {
  if (question?.type === 'checkbox') return value === true ? 'yes' : 'no';
  if (value === null || value === undefined) return '';
  return Array.isArray(value) ? value.join(', ') : String(value);
}
function resolveCurrentAccountLabel(profile = {}, fallback = 'Current account') {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  if (first) return first;
  const displayName = typeof profile?.displayName === 'string' ? profile.displayName.trim() : '';
  if (displayName) return displayName;
  const username = typeof profile?.username === 'string' ? profile.username.trim() : '';
  return username || fallback;
}

function normalizeStoredQuestionValue(question, value) {
  if (question?.type === 'checkbox') return String(value || '').trim().toLowerCase() === 'yes';
  return value === null || value === undefined ? '' : String(value);
}

export default function JoinLearnToRow() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const preselectedCourseId = String(searchParams.get('courseId') || '').trim();
  const checkoutState = String(searchParams.get('checkout') || '').trim().toLowerCase();
  const checkoutSessionId = String(searchParams.get('session_id') || '').trim();
  const queryTargetUserId = String(searchParams.get('targetUserId') || '').trim();
  const {
    teamId,
    team,
    user,
    status: joinStatus,
    setStatus,
    familyMembershipGroups,
    hasPaid,
    requiredJoinDocuments,
    stripeSetup,
  } = useJoin();

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(preselectedCourseId);
  const [targetMode, setTargetMode] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [selectedTargetUserId, setSelectedTargetUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState('');
  const [clubJoinQuestions, setClubJoinQuestions] = useState([]);
  const [learnToRowJoinMode, setLearnToRowJoinMode] = useState('club_join_form');
  const [learnToRowQuestions, setLearnToRowQuestions] = useState([]);
  const [learnToRowJoinPricing, setLearnToRowJoinPricing] = useState({ indicativePriceMajor: '', indicativePriceNote: '' });
  const [storedFormResponses, setStoredFormResponses] = useState([]);
  const [questionResponses, setQuestionResponses] = useState({});
  const [acceptedDocuments, setAcceptedDocuments] = useState({});
  const [sessionResponses, setSessionResponses] = useState({});
  const [joinedEventSessions, setJoinedEventSessions] = useState([]);
  const [releasedToPayment, setReleasedToPayment] = useState(false);
  const [passingReleasedPlace, setPassingReleasedPlace] = useState(false);
  const [courseAcknowledged, setCourseAcknowledged] = useState(false);
  const [currentAccountLabel, setCurrentAccountLabel] = useState('');
  const [membershipDiscountCode, setMembershipDiscountCode] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [appliedPricingPreview, setAppliedPricingPreview] = useState(null);
  const [discountMessage, setDiscountMessage] = useState('');
  const [checkoutConfirmationTimedOut, setCheckoutConfirmationTimedOut] = useState(false);
  const [checkoutIntentStatus, setCheckoutIntentStatus] = useState('');
  const [checkoutReconciliationRetryKey, setCheckoutReconciliationRetryKey] = useState(0);
  const teamWaitlistOpen = Boolean(team?.modules?.rowing?.learnToRow?.waitlistOpen);
  const coursePaymentSetupReady = isCoursePaymentSetupComplete(stripeSetup || {});
  const checkoutReturnActive = checkoutState === 'success' || Boolean(checkoutSessionId);
  const coursePaymentSetupBlocked = !coursePaymentSetupReady && !checkoutReturnActive;
  const selfAlreadyInClub = Boolean(hasPaid);
  const activeAccountName = currentAccountLabel || 'Current account';
  const activeAccountSummaryLabel = activeAccountName === 'Current account' ? 'the current account' : activeAccountName;
  const childAccountReturnPath = useMemo(() => {
    const params = new URLSearchParams({ joinMode: 'learn-to-row' });
    const returnCourseId = selectedCourseId || preselectedCourseId;
    if (returnCourseId) params.set('courseId', returnCourseId);
    return `/join/${teamId}?${params.toString()}`;
  }, [preselectedCourseId, selectedCourseId, teamId]);
  const checkoutIntentFailed = CHECKOUT_FAILURE_STATUSES.has(checkoutIntentStatus);
  const checkoutActivationFailed = checkoutIntentStatus === 'activation_failed';

  useEffect(() => {
    if (checkoutState === 'success' || outcome) return;
    if (joinStatus === 'payment' || joinStatus === 'joined') {
      setStatus('form');
    }
  }, [checkoutState, joinStatus, outcome, setStatus]);

  useEffect(() => {
    if (outcome === 'joined') {
      setStatus('joined');
      return;
    }
    if (checkoutState === 'success') {
      setStatus('payment');
    }
  }, [checkoutState, outcome, setStatus]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const preselectedCoursePromise = preselectedCourseId
          ? withTimeout(
            getDoc(doc(db, 'teams', teamId, 'learnToRowCourses', preselectedCourseId)).catch(() => null),
            FIRESTORE_READ_TIMEOUT_MS,
            null
          )
          : Promise.resolve(null);
        const [publicCourses, joinFormSnap, learnToRowJoinSnap, preselectedSnap] = await Promise.all([
          withTimeout(loadPublicLearnToRowCourses(teamId), FIRESTORE_READ_TIMEOUT_MS, []),
          withTimeout(getDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config')).catch(() => null), FIRESTORE_READ_TIMEOUT_MS, null),
          withTimeout(getDoc(doc(db, 'teams', teamId, 'learnToRowJoinConfig', 'config')).catch(() => null), FIRESTORE_READ_TIMEOUT_MS, null),
          preselectedCoursePromise,
        ]);
        let nextCourses = Array.isArray(publicCourses) ? publicCourses : [];

        if (preselectedCourseId && !nextCourses.some((course) => course.id === preselectedCourseId) && preselectedSnap?.exists?.()) {
          const preselectedCourse = normalizeCourseSnapshot(
            { id: preselectedSnap.id, ...preselectedSnap.data() },
            preselectedSnap.id
          );
          if (preselectedCourse) {
            nextCourses = [preselectedCourse, ...nextCourses];
          }
        }

        nextCourses = nextCourses
          .filter((course, index, entries) => entries.findIndex((entry) => entry.id === course.id) === index)
          .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
        if (cancelled) return;
        setClubJoinQuestions(normalizeLearnToRowQuestions(joinFormSnap?.exists() ? (joinFormSnap.data()?.questions || []) : []));
        setLearnToRowJoinMode(normalizeJoinMode(learnToRowJoinSnap?.exists() ? learnToRowJoinSnap.data()?.questionMode : 'club_join_form'));
        setLearnToRowQuestions(normalizeLearnToRowQuestions(learnToRowJoinSnap?.exists() ? (learnToRowJoinSnap.data()?.questions || []) : []));
        setLearnToRowJoinPricing({
          indicativePriceMajor: typeof learnToRowJoinSnap?.data?.()?.indicativePriceMajor === 'string'
            ? learnToRowJoinSnap.data().indicativePriceMajor
            : (Number.isFinite(Number(learnToRowJoinSnap?.data?.()?.indicativePriceMinor))
              ? String((Number(learnToRowJoinSnap.data().indicativePriceMinor || 0) / 100).toFixed(2))
              : ''),
          indicativePriceNote: typeof learnToRowJoinSnap?.data?.()?.indicativePriceNote === 'string'
            ? learnToRowJoinSnap.data().indicativePriceNote
            : '',
        });
        setCourses((current) => {
          if (
            preselectedCourseId
            && !nextCourses.some((course) => course.id === preselectedCourseId)
          ) {
            const existingCheckoutCourse = current.find((course) => course?.id === preselectedCourseId);
            if (existingCheckoutCourse) return upsertCourse(nextCourses, existingCheckoutCourse);
          }
          return nextCourses;
        });
        setSelectedCourseId((current) => {
          if (preselectedCourseId) {
            return preselectedCourseId;
          }
          if (current && nextCourses.some((course) => course.id === current)) return current;
          if (nextCourses[0]?.id) return nextCourses[0].id;
          return teamWaitlistOpen ? '' : '';
        });
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load Learn to Row courses.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [checkoutState, preselectedCourseId, teamId, teamWaitlistOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadCurrentAccountLabel = async () => {
      if (!user?.uid) {
        if (!cancelled) setCurrentAccountLabel('');
        return;
      }
      const fallbackLabel = user?.displayName?.trim() || user?.email?.trim() || 'Current account';
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid)).catch(() => null);
        const profile = userSnap?.exists() ? userSnap.data() || {} : {};
        if (!cancelled) {
          setCurrentAccountLabel(resolveCurrentAccountLabel(profile, fallbackLabel));
        }
      } catch {
        if (!cancelled) {
          setCurrentAccountLabel(fallbackLabel);
        }
      }
    };
    loadCurrentAccountLabel();
    return () => {
      cancelled = true;
    };
  }, [user?.displayName, user?.email, user?.uid]);

  useEffect(() => {
    setQuestionResponses({});
  }, [learnToRowJoinMode, selectedCourseId, selectedTargetUserId]);

  useEffect(() => {
    if (!queryTargetUserId || !user?.uid || queryTargetUserId === user.uid || familyMembershipGroups.length === 0) return;
    const matchingFamily = familyMembershipGroups.find((group) => (
      Array.isArray(group?.members) && group.members.some((member) => member.uid === queryTargetUserId)
    ));
    if (!matchingFamily) return;
    setTargetMode('family');
    setSelectedFamilyId(matchingFamily.familyId || '');
    setSelectedTargetUserId(queryTargetUserId);
  }, [familyMembershipGroups, queryTargetUserId, user?.uid]);

  useEffect(() => {
    if (!queryTargetUserId || !user?.uid) return;
    if (queryTargetUserId === user.uid && selfAlreadyInClub) {
      setTargetMode('');
      setSelectedTargetUserId('');
      return;
    }
    if (queryTargetUserId === user.uid) {
      setTargetMode('me');
      setSelectedTargetUserId(user.uid);
      setSelectedFamilyId('');
    }
  }, [queryTargetUserId, selfAlreadyInClub, user?.uid]);

  useEffect(() => {
    if (!teamId || !checkoutSessionId || checkoutState !== 'success') return undefined;
    const intentRef = doc(db, 'teams', teamId, 'learnToRowCheckoutIntents', checkoutSessionId);
    const statusRef = doc(db, 'teams', teamId, 'learnToRowCheckoutStatuses', checkoutSessionId);
    let cancelled = false;
    let resolved = false;
    let attempts = 0;
    let pollTimer;
    let timeoutTimer;
    setCheckoutConfirmationTimedOut(false);
    const applyIntent = (data = {}) => {
      if (cancelled) return;
      const intentStatus = normalizeCheckoutIntentStatus(data?.status);
      if (intentStatus) setCheckoutIntentStatus(intentStatus);
      const courseSnapshot = normalizeCourseSnapshot(data?.courseSnapshot, data?.courseId || preselectedCourseId);
      if (courseSnapshot) {
        setCourses((current) => upsertCourse(current, courseSnapshot));
        setSelectedCourseId(courseSnapshot.id);
      }
      if (intentStatus === 'completed') {
        resolved = true;
        setOutcome('joined');
        setError('');
        setCheckoutConfirmationTimedOut(false);
        return;
      }
      if (intentStatus === 'expired' || intentStatus === 'payment_failed') {
        resolved = true;
        setError(intentStatus === 'expired'
          ? 'This payment session expired before the Learn to Row place was confirmed.'
          : 'Stripe reported that this Learn to Row payment failed.');
        setCheckoutConfirmationTimedOut(false);
        return;
      }
      if (intentStatus === 'activation_failed') {
        resolved = true;
        setError('Payment was received, but the Learn to Row place could not be activated automatically. Please contact the club.');
        setCheckoutConfirmationTimedOut(false);
      }
    };
    const readCheckoutDoc = async (ref) => {
      try {
        const snapshot = await withTimeout(getDoc(ref), FIRESTORE_READ_TIMEOUT_MS, null);
        if (!snapshot) {
          return null;
        }
        return snapshot;
      } catch {
        return null;
      }
    };
    const pollIntent = async () => {
      if (cancelled || resolved) return;
      attempts += 1;
      const [intentSnapshot, statusSnapshot] = await Promise.all([
        readCheckoutDoc(intentRef),
        readCheckoutDoc(statusRef),
      ]);
      if (cancelled || resolved) return;
      if (intentSnapshot?.exists?.()) {
        applyIntent(intentSnapshot.data() || {});
      }
      if (!resolved && statusSnapshot?.exists?.()) {
        applyIntent(statusSnapshot.data() || {});
      }
      if (!resolved && attempts < CHECKOUT_INTENT_MAX_ATTEMPTS) {
        pollTimer = setTimeout(pollIntent, CHECKOUT_INTENT_POLL_MS);
      } else if (!resolved) {
        setCheckoutConfirmationTimedOut(true);
      }
    };
    const unsubscribeIntent = safeOnSnapshot(
      intentRef,
      (snapshot) => {
        if (snapshot.exists()) applyIntent(snapshot.data() || {});
      },
      () => {
        pollIntent();
      }
    );
    const unsubscribeStatus = safeOnSnapshot(
      statusRef,
      (snapshot) => {
        if (snapshot.exists()) applyIntent(snapshot.data() || {});
      },
      () => {
        pollIntent();
      }
    );
    pollTimer = setTimeout(pollIntent, 0);
    timeoutTimer = setTimeout(() => {
      if (!resolved && !cancelled) {
        setCheckoutConfirmationTimedOut(true);
      }
    }, CHECKOUT_INTENT_MAX_ATTEMPTS * CHECKOUT_INTENT_POLL_MS + FIRESTORE_READ_TIMEOUT_MS);
    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(timeoutTimer);
      unsubscribeIntent();
      unsubscribeStatus();
    };
  }, [checkoutReconciliationRetryKey, checkoutSessionId, checkoutState, preselectedCourseId, teamId]);

  useEffect(() => {
    const targetIsCurrentUser = Boolean(user?.uid && queryTargetUserId && queryTargetUserId === user.uid);
    if (!teamId || !queryTargetUserId || checkoutState !== 'success' || !targetIsCurrentUser) return undefined;
    const memberRef = doc(db, 'teams', teamId, 'members', queryTargetUserId);
    const settingsRef = doc(db, 'users', queryTargetUserId, 'teamSettings', teamId);
    let cancelled = false;
    let resolved = false;
    let attempts = 0;
    let pollTimer;
    const applyActivationDoc = (data = {}) => {
      if (cancelled || resolved) return;
      const learnToRowState = data?.learnToRow && typeof data.learnToRow === 'object'
        ? data.learnToRow
        : {};
      const status = normalizeParticipantStatus(learnToRowState?.status);
      const courseSnapshot = normalizeCourseSnapshot(
        learnToRowState?.courseSnapshot,
        learnToRowState?.courseId || preselectedCourseId
      );
      if (courseSnapshot) {
        setCourses((current) => upsertCourse(current, courseSnapshot));
        setSelectedCourseId(courseSnapshot.id);
      }
      if (status === 'active_course') {
        resolved = true;
        setOutcome('joined');
        setError('');
        setCheckoutConfirmationTimedOut(false);
      }
    };
    const readActivationDoc = async (ref) => {
      try {
        const snapshot = await withTimeout(getDoc(ref), FIRESTORE_READ_TIMEOUT_MS, null);
        if (!snapshot) {
          return null;
        }
        return snapshot;
      } catch {
        return null;
      }
    };
    const pollActivationDocs = async () => {
      if (cancelled || resolved) return;
      attempts += 1;
      const [memberSnapshot, settingsSnapshot] = await Promise.all([
        readActivationDoc(memberRef),
        readActivationDoc(settingsRef),
      ]);
      if (cancelled || resolved) return;
      if (memberSnapshot?.exists?.()) applyActivationDoc(memberSnapshot.data() || {});
      if (!resolved && settingsSnapshot?.exists?.()) applyActivationDoc(settingsSnapshot.data() || {});
      if (!resolved && attempts < CHECKOUT_PARTICIPANT_MAX_ATTEMPTS) {
        pollTimer = setTimeout(pollActivationDocs, CHECKOUT_PARTICIPANT_POLL_MS);
      }
    };
    const unsubscribeMember = safeOnSnapshot(
      memberRef,
      (snapshot) => {
        if (snapshot.exists()) applyActivationDoc(snapshot.data() || {});
      },
      () => {}
    );
    const unsubscribeSettings = safeOnSnapshot(
      settingsRef,
      (snapshot) => {
        if (snapshot.exists()) applyActivationDoc(snapshot.data() || {});
      },
      () => {}
    );
    pollTimer = setTimeout(pollActivationDocs, 0);
    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      unsubscribeMember();
      unsubscribeSettings();
    };
  }, [checkoutReconciliationRetryKey, checkoutState, preselectedCourseId, queryTargetUserId, teamId, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (selfAlreadyInClub && targetMode === 'me') {
      setTargetMode('');
      setSelectedTargetUserId('');
      return;
    }
    if (targetMode === 'me') {
      setSelectedTargetUserId(user.uid);
      return;
    }
    if (!targetMode) {
      setSelectedTargetUserId('');
      setSelectedFamilyId('');
      return;
    }
    if (!selectedFamilyId && familyMembershipGroups[0]?.familyId) {
      setSelectedFamilyId(familyMembershipGroups[0].familyId);
    }
  }, [familyMembershipGroups, selectedFamilyId, selfAlreadyInClub, targetMode, user?.uid]);

  useEffect(() => {
    if (!teamId || !selectedCourseId || !queryTargetUserId || checkoutState !== 'success') return undefined;
    setCheckoutConfirmationTimedOut(false);
    let cancelled = false;
    let resolved = false;
    let attempts = 0;
    let pollTimer;
    let timeoutTimer;
    const participantRef = doc(db, 'teams', teamId, 'learnToRowCourses', selectedCourseId, 'participants', queryTargetUserId);
    const applyParticipant = (data = {}) => {
      const status = normalizeParticipantStatus(data?.status);
      const courseSnapshot = normalizeCourseSnapshot(data?.courseSnapshot, data?.courseId || selectedCourseId);
      if (courseSnapshot) {
        setCourses((current) => upsertCourse(current, courseSnapshot));
        setSelectedCourseId(courseSnapshot.id);
      }
      if (status === 'active_course') {
        resolved = true;
        setOutcome('joined');
        setError('');
        setCheckoutConfirmationTimedOut(false);
      } else if (status === 'waitlisted') {
        resolved = true;
        setOutcome('waitlisted');
        setError('');
        setCheckoutConfirmationTimedOut(false);
      }
    };
    const pollParticipant = async () => {
      if (cancelled || resolved) return;
      attempts += 1;
      const snapshot = await withTimeout(getDoc(participantRef).catch(() => null), FIRESTORE_READ_TIMEOUT_MS, null);
      if (cancelled || resolved) return;
      if (snapshot?.exists?.()) {
        applyParticipant(snapshot.data() || {});
      }
      if (!resolved && attempts < CHECKOUT_PARTICIPANT_MAX_ATTEMPTS) {
        pollTimer = setTimeout(pollParticipant, CHECKOUT_PARTICIPANT_POLL_MS);
      } else if (!resolved) {
        setCheckoutConfirmationTimedOut(true);
      }
    };
    const unsubscribe = safeOnSnapshot(
      participantRef,
      (snapshot) => {
        if (snapshot.exists()) applyParticipant(snapshot.data() || {});
      },
      () => {
        pollParticipant();
      }
    );
    pollTimer = setTimeout(pollParticipant, 0);
    timeoutTimer = setTimeout(() => {
      if (!resolved && !cancelled) {
        setCheckoutConfirmationTimedOut(true);
      }
    }, CHECKOUT_PARTICIPANT_MAX_ATTEMPTS * CHECKOUT_PARTICIPANT_POLL_MS + FIRESTORE_READ_TIMEOUT_MS);
    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(timeoutTimer);
      unsubscribe();
    };
  }, [checkoutReconciliationRetryKey, checkoutState, queryTargetUserId, selectedCourseId, teamId]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) || null,
    [courses, selectedCourseId]
  );
  const effectiveTargetUserId = queryTargetUserId || selectedTargetUserId || user?.uid || '';
  const courseSessions = useMemo(
    () => normalizeSessions(selectedCourse?.sessions || []),
    [selectedCourse?.sessions]
  );
  const effectiveJoinedSessions = useMemo(
    () => (joinedEventSessions.length > 0 ? joinedEventSessions : courseSessions),
    [courseSessions, joinedEventSessions]
  );
  const requiresSessionRsvps = selectedCourse?.requireSessionRsvp !== false;
  const selectedFamily = useMemo(
    () => familyMembershipGroups.find((group) => group.familyId === selectedFamilyId) || null,
    [familyMembershipGroups, selectedFamilyId]
  );
  const selectedFamilyMembers = useMemo(
    () => Array.isArray(selectedFamily?.members) ? selectedFamily.members : [],
    [selectedFamily]
  );
  const selectedTargetFamilyMember = useMemo(
    () => selectedFamilyMembers.find((member) => member?.uid === effectiveTargetUserId) || null,
    [effectiveTargetUserId, selectedFamilyMembers]
  );
  const effectiveTargetIsCurrentAccount = Boolean(user?.uid && effectiveTargetUserId === user.uid);
  const targetAccountLabel = effectiveTargetIsCurrentAccount
    ? activeAccountName
    : (selectedTargetFamilyMember?.name || 'the selected family account');
  const selectedCoursePricePreview = useMemo(
    () => selectedCourse
      ? calculateLearnToRowCheckoutPreview({
          courseFeeMinor: Number(selectedCourse.courseFeeMinor || 0),
          currency: stripeSetup?.currency || 'GBP',
          teamFeeCfg: stripeSetup || {},
          learnToRowModule: team?.modules?.rowing?.learnToRow || {},
          course: selectedCourse,
        })
      : null,
    [selectedCourse, stripeSetup, team?.modules?.rowing?.learnToRow]
  );
  const normalizedDiscountCode = membershipDiscountCode.trim().toUpperCase();
  const activePricingPreview = appliedPricingPreview
    && appliedPricingPreview.code === normalizedDiscountCode
    && appliedPricingPreview.courseId === selectedCourseId
    && appliedPricingPreview.targetUserId === effectiveTargetUserId
    ? appliedPricingPreview
    : null;
  const effectiveCoursePricePreview = activePricingPreview?.breakdown || selectedCoursePricePreview;
  const effectiveDiscountAppliedMinor = Number(activePricingPreview?.membershipDiscountAppliedMinor || 0);
  const effectiveStoredCreditAppliedMinor = Number(activePricingPreview?.membershipStoredCreditAppliedMinor || 0);
  const effectiveStoredCreditRemainingMinor = Number(activePricingPreview?.membershipStoredCreditRemainingMinor || 0);

  useEffect(() => {
    if (!preselectedCourseId || selectedCourse || !teamId || !effectiveTargetUserId) return undefined;
    let cancelled = false;
    const loadReleasedCoursePreview = async () => {
      try {
        const createCheckout = httpsCallable(functions, 'createLearnToRowCheckout');
        const result = await createCheckout({
          previewOnly: true,
          teamId,
          courseId: preselectedCourseId,
          targetUserId: effectiveTargetUserId,
          targetFamilyId: targetMode === 'family' ? selectedFamilyId || '' : '',
          allowWaitlist: true,
        });
        if (cancelled) return;
        const data = result?.data || {};
        const courseSnapshot = normalizeCourseSnapshot(data?.courseSnapshot, data?.courseId || preselectedCourseId);
        if (courseSnapshot) {
          setCourses((current) => upsertCourse(current, courseSnapshot));
          setSelectedCourseId(courseSnapshot.id);
        }
      } catch {
        // The user can still retry payment once Firestore/permission state catches up.
      }
    };
    loadReleasedCoursePreview();
    return () => {
      cancelled = true;
    };
  }, [effectiveTargetUserId, preselectedCourseId, selectedCourse, selectedFamilyId, targetMode, teamId]);
  const effectiveJoinQuestions = useMemo(() => {
    if (learnToRowJoinMode === 'l2r_only') return learnToRowQuestions;
    if (learnToRowJoinMode === 'club_join_form_plus_l2r') {
      const byId = new Map();
      [...clubJoinQuestions, ...learnToRowQuestions].forEach((question) => {
        if (!question?.id) return;
        byId.set(question.id, question);
      });
      return Array.from(byId.values());
    }
    return clubJoinQuestions;
  }, [clubJoinQuestions, learnToRowJoinMode, learnToRowQuestions]);
  const missingRequiredQuestions = useMemo(
    () => effectiveJoinQuestions.filter((question) => {
      if (question.required !== true) return false;
      const value = questionResponses?.[question.id];
      if (question.type === 'checkbox') return value !== true;
      return String(value || '').trim() === '';
    }),
    [effectiveJoinQuestions, questionResponses]
  );
  const allSessionsAnswered = useMemo(
    () => !requiresSessionRsvps
      || effectiveJoinedSessions.length === 0
      || effectiveJoinedSessions.every((session) => normalizeRsvpStatus(sessionResponses?.[session.id])),
    [effectiveJoinedSessions, requiresSessionRsvps, sessionResponses]
  );
  const missingAcceptedDocuments = useMemo(
    () => (Array.isArray(requiredJoinDocuments) ? requiredJoinDocuments : []).filter((docItem) => docItem?.id && acceptedDocuments?.[docItem.id] !== true),
    [acceptedDocuments, requiredJoinDocuments]
  );
  const effectiveQuestionTargetUserId = selectedTargetUserId || queryTargetUserId || user?.uid || '';
  const requiresCourseAcknowledgement = Boolean(selectedCourseId);

  useEffect(() => {
    if (!normalizedDiscountCode) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      return;
    }
    if (appliedPricingPreview && !activePricingPreview) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
    }
  }, [activePricingPreview, appliedPricingPreview, normalizedDiscountCode]);

  useEffect(() => {
    setAppliedPricingPreview(null);
    setDiscountMessage('');
  }, [effectiveTargetUserId, selectedCourseId, selectedFamilyId, targetMode]);

  const handleOpenClub = useCallback(() => {
    if (!teamId) return;
    navigate(`/teams/${teamId}`, { replace: true });
  }, [navigate, teamId]);

  const handleRetryCheckoutConfirmation = useCallback(() => {
    setCheckoutConfirmationTimedOut(false);
    setCheckoutReconciliationRetryKey((current) => current + 1);
  }, []);

  const handleSessionStatusChange = useCallback((sessionId, status) => {
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;
    const normalizedStatus = normalizeRsvpStatus(status);
    setSessionResponses((current) => (
      current?.[normalizedSessionId] === normalizedStatus
        ? current
        : { ...current, [normalizedSessionId]: normalizedStatus }
    ));
  }, []);

  const handleOpenTargetAccountClub = useCallback(() => {
    navigate('/profile?tab=family');
  }, [navigate]);

  useEffect(() => {
    setCourseAcknowledged(false);
  }, [selectedCourseId, effectiveQuestionTargetUserId, releasedToPayment]);

  useEffect(() => {
    if (!teamId || !effectiveQuestionTargetUserId) {
      setStoredFormResponses([]);
      setReleasedToPayment(false);
      return;
    }
    let cancelled = false;
    const loadStoredResponses = async () => {
      try {
        const [waitlistSnap, participantSnap] = await Promise.all([
          withTimeout(
            getDoc(doc(db, 'teams', teamId, 'learnToRowWaitlist', effectiveQuestionTargetUserId)).catch(() => null),
            FIRESTORE_READ_TIMEOUT_MS,
            null
          ),
          selectedCourseId
            ? withTimeout(
              getDoc(doc(db, 'teams', teamId, 'learnToRowCourses', selectedCourseId, 'participants', effectiveQuestionTargetUserId)).catch(() => null),
              FIRESTORE_READ_TIMEOUT_MS,
              null
            )
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const participantStatus = normalizeParticipantStatus(participantSnap?.data()?.status);
        const waitlistStatus = normalizeParticipantStatus(waitlistSnap?.data()?.status);
        setReleasedToPayment(participantStatus === 'released_waitlist');
        if (participantStatus === 'active_course') setOutcome('joined');
        else if (waitlistStatus === 'awaiting_approval' && participantStatus !== 'released_waitlist') setOutcome('awaitingApproval');
        else if (waitlistStatus === 'waitlisted' && participantStatus !== 'released_waitlist') setOutcome('waitlisted');
        const nextResponses = Array.isArray(participantSnap?.data()?.formResponses)
          ? participantSnap.data().formResponses
          : (Array.isArray(waitlistSnap?.data()?.formResponses) ? waitlistSnap.data().formResponses : []);
        const storedAcceptances = Array.isArray(participantSnap?.data()?.documentAcceptances)
          ? participantSnap.data().documentAcceptances
          : (Array.isArray(waitlistSnap?.data()?.documentAcceptances) ? waitlistSnap.data().documentAcceptances : []);
        setAcceptedDocuments(
          Object.fromEntries(
            storedAcceptances
              .map((entry) => [String(entry?.documentId || '').trim(), true])
              .filter(([documentId]) => Boolean(documentId))
          )
        );
        setStoredFormResponses(nextResponses);
      } catch {
        if (!cancelled) {
          setStoredFormResponses([]);
          setAcceptedDocuments({});
          setReleasedToPayment(false);
        }
      }
    };
    loadStoredResponses();
    return () => {
      cancelled = true;
    };
  }, [effectiveQuestionTargetUserId, selectedCourseId, teamId]);

  useEffect(() => {
    if (effectiveJoinQuestions.length === 0) {
      setQuestionResponses({});
      return;
    }
    const storedById = new Map(
      (Array.isArray(storedFormResponses) ? storedFormResponses : [])
        .map((response) => [String(response?.id || '').trim(), response?.value])
        .filter(([id]) => Boolean(id))
    );
    const nextResponses = {};
    effectiveJoinQuestions.forEach((question) => {
      if (!storedById.has(question.id)) return;
      nextResponses[question.id] = normalizeStoredQuestionValue(question, storedById.get(question.id));
    });
    setQuestionResponses(nextResponses);
  }, [effectiveJoinQuestions, storedFormResponses]);

  const updateQuestionResponse = (questionId, value) => {
    setQuestionResponses((current) => ({ ...current, [questionId]: value }));
  };

  const handleApplyDiscount = useCallback(async () => {
    const trimmedCode = membershipDiscountCode.trim().toUpperCase();
    if (!trimmedCode) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      setError('');
      return;
    }
    if (!selectedCourseId) {
      setError('Choose a Learn to Row course before applying a discount.');
      return;
    }
    if (!selectedTargetUserId) {
      setError('Choose which account the Learn to Row place is for before applying a discount.');
      return;
    }
    setApplyingDiscount(true);
    setError('');
    setDiscountMessage('');
    try {
      const createCheckout = httpsCallable(functions, 'createLearnToRowCheckout');
      const result = await createCheckout({
        previewOnly: true,
        teamId,
        courseId: selectedCourseId,
        targetUserId: selectedTargetUserId,
        targetFamilyId: targetMode === 'family' ? selectedFamilyId || '' : '',
        allowWaitlist: true,
        membershipDiscountCode: trimmedCode,
      });
      const data = result?.data || {};
      const courseSnapshot = normalizeCourseSnapshot(data?.courseSnapshot, data?.courseId || selectedCourseId);
      if (courseSnapshot) {
        setCourses((current) => upsertCourse(current, courseSnapshot));
        setSelectedCourseId(courseSnapshot.id);
      }
      setAppliedPricingPreview({
        code: trimmedCode,
        courseId: data?.courseId || selectedCourseId,
        targetUserId: selectedTargetUserId,
        breakdown: {
          baseAmountMinor: Number(data?.baseAmount || 0),
          chargedPlatformFeeMinor: Number(data?.chargedPlatformFeeMinor || data?.platformFee || 0),
          chargedStripeFeeMinor: Number(data?.chargedStripeFeeMinor || data?.stripeFee || 0),
          totalDueMinor: Number(data?.finalAmount || 0),
        },
        membershipDiscountAppliedMinor: Number(data?.membershipDiscountAppliedMinor || 0),
        membershipStoredCreditAppliedMinor: Number(data?.membershipStoredCreditAppliedMinor || 0),
        membershipStoredCreditRemainingMinor: Number(data?.membershipStoredCreditRemainingMinor || 0),
      });
      setDiscountMessage(
        Number(data?.membershipDiscountAppliedMinor || 0) > 0 || Number(data?.membershipStoredCreditAppliedMinor || 0) > 0
          ? 'Discount applied.'
          : 'Code accepted.'
      );
    } catch (err) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      setError(err?.message || 'Could not apply that discount code.');
    } finally {
      setApplyingDiscount(false);
    }
  }, [membershipDiscountCode, selectedCourseId, selectedFamilyId, selectedTargetUserId, targetMode, teamId]);

  useEffect(() => {
    if (outcome !== 'joined' || !teamId || !selectedCourseId) {
      setJoinedEventSessions([]);
      return undefined;
    }
    let cancelled = false;
    const loadJoinedEventSessions = async () => {
      try {
        const eventsSnap = await withTimeout(
          getDocs(query(
            collection(db, 'teams', teamId, 'events'),
            where('learnToRowCourseId', '==', selectedCourseId)
          )),
          FIRESTORE_READ_TIMEOUT_MS,
          null
        );
        if (cancelled) return;
        const nextSessions = (eventsSnap?.docs || [])
          .map((docSnap) => {
            const data = docSnap.data() || {};
            const start = data?.start && typeof data.start.toDate === 'function'
              ? data.start.toDate()
              : new Date(`${data?.date || ''}T${data?.time || ''}`);
            const end = data?.end && typeof data.end.toDate === 'function' ? data.end.toDate() : null;
            const date = data?.date || (start instanceof Date && !Number.isNaN(start.getTime()) ? start.toISOString().slice(0, 10) : '');
            const startTime = data?.time || (start instanceof Date && !Number.isNaN(start.getTime())
              ? start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
              : '');
            const endTime = end instanceof Date && !Number.isNaN(end.getTime())
              ? end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
              : startTime;
            return {
              id: typeof data?.learnToRowSessionId === 'string' && data.learnToRowSessionId.trim()
                ? data.learnToRowSessionId.trim()
                : docSnap.id,
              eventId: docSnap.id,
              date,
              startTime,
              endTime,
            };
          })
          .filter((session) => session.date && session.startTime && session.endTime)
          .sort((left, right) => `${left.date}T${left.startTime}`.localeCompare(`${right.date}T${right.startTime}`));
        setJoinedEventSessions(nextSessions);
      } catch {
        if (!cancelled) setJoinedEventSessions([]);
      }
    };
    loadJoinedEventSessions();
    return () => {
      cancelled = true;
    };
  }, [outcome, selectedCourseId, teamId]);

  useEffect(() => {
    if (outcome !== 'joined') {
      setSessionResponses({});
      return;
    }
    setSessionResponses((current) => {
      const next = {};
      effectiveJoinedSessions.forEach((session) => {
        next[session.id] = current?.[session.id] || '';
      });
      return next;
    });
  }, [effectiveJoinedSessions, outcome]);

  const renderJoinQuestionInput = (question) => {
    const value = questionResponses?.[question.id];
    if (question.type === 'textarea') {
      return (
        <textarea
          className="form-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
          required={question.required}
          placeholder={getFormFieldPlaceholder(question)}
        />
      );
    }
    if (question.type === 'number') {
      return (
        <input
          type="number"
          className="form-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
          required={question.required}
          placeholder={getFormFieldPlaceholder(question)}
        />
      );
    }
    if (question.type === 'date') {
      return (
        <input
          type="date"
          className="form-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
          required={question.required}
        />
      );
    }
    if (question.type === 'select') {
      return (
        <select
          className="form-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
          required={question.required}
        >
          <option value="">{CHOOSE_OPTION_LABEL}</option>
          {(question.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    if (question.type === 'yesno') {
      return (
        <select
          className="form-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
          required={question.required}
        >
          <option value="">{CHOOSE_OPTION_LABEL}</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    }
    if (question.type === 'checkbox') {
      return (
        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={value === true}
            onChange={(event) => updateQuestionResponse(question.id, event.target.checked)}
            required={question.required}
            style={{ marginTop: '0.2rem' }}
          />
          <span>{question.label}</span>
        </label>
      );
    }
    return (
      <input
        className="form-input"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => updateQuestionResponse(question.id, event.target.value)}
        required={question.required}
        placeholder={getFormFieldPlaceholder(question)}
      />
    );
  };

  const handleStart = useCallback(async ({ skipEditableChecks = false } = {}) => {
    if (coursePaymentSetupBlocked) {
      setError(coursePaymentSetupMessage('Learn to Row signup'));
      return;
    }
    if (!selectedCourseId && !teamWaitlistOpen) {
      setError('Choose a Learn to Row course first.');
      return;
    }
    if (!selectedTargetUserId) {
      setError('Choose which account the Learn to Row place is for.');
      return;
    }
    if (requiresCourseAcknowledgement && !courseAcknowledged) {
      setError('Please confirm that you have checked the Learn to Row course dates before continuing.');
      return;
    }
    if (selfAlreadyInClub && targetMode === 'me') {
      setError('Your account is already in the club. Choose someone in your family if this Learn to Row place is for a child.');
      return;
    }
    if (!skipEditableChecks && missingRequiredQuestions.length > 0) {
      setError('Complete all required Learn to Row join questions before continuing.');
      return;
    }
    if (!skipEditableChecks && missingAcceptedDocuments.length > 0) {
      setError('You must accept all required club documents before continuing.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const createCheckout = httpsCallable(functions, 'createLearnToRowCheckout');
      const result = await createCheckout({
        teamId,
        ...(selectedCourseId ? { courseId: selectedCourseId } : {}),
        targetUserId: selectedTargetUserId,
        targetFamilyId: targetMode === 'family' ? selectedFamilyId || '' : '',
        allowWaitlist: true,
        membershipDiscountCode: membershipDiscountCode.trim().toUpperCase(),
        formResponses: effectiveJoinQuestions.map((question) => ({
          id: question.id,
          label: question.label,
          type: question.type,
          syncToProfile: question.syncToProfile === true,
          value: normalizeQuestionValueForStorage(question, questionResponses?.[question.id]),
        })),
        documentAcceptances: (Array.isArray(requiredJoinDocuments) ? requiredJoinDocuments : [])
          .filter((docItem) => docItem?.id && acceptedDocuments?.[docItem.id] === true)
          .map((docItem) => ({
            documentId: docItem.id,
            title: docItem.title || '',
            versionLabel: docItem.versionLabel || '',
            sourceUrl: getDocumentSourceUrl(docItem),
            acceptedAt: new Date().toISOString(),
          })),
      });
      const data = result?.data || {};
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.waitlisted) {
        setOutcome('waitlisted');
        return;
      }
      if (data?.awaitingApproval) {
        setOutcome('awaitingApproval');
        return;
      }
      if (data?.joined) {
        setOutcome('joined');
        return;
      }
      throw new Error('Unexpected Learn to Row response.');
    } catch (err) {
      setError(err?.message || 'Could not continue to Learn to Row checkout.');
    } finally {
      setSubmitting(false);
    }
  }, [
    acceptedDocuments,
    effectiveJoinQuestions,
    missingAcceptedDocuments.length,
    missingRequiredQuestions.length,
    requiredJoinDocuments,
    selectedCourseId,
    selectedFamilyId,
    selectedTargetUserId,
    courseAcknowledged,
    coursePaymentSetupBlocked,
    requiresCourseAcknowledgement,
    selfAlreadyInClub,
    targetMode,
    teamId,
    teamWaitlistOpen,
    membershipDiscountCode,
    questionResponses,
  ]);

  const handlePassReleasedPlace = useCallback(async () => {
    if (!releasedToPayment || !teamId || !selectedCourseId || !effectiveTargetUserId) return;
    const confirmed = window.confirm('Pass on this Learn to Row course and return to the live waitlist for a future place?');
    if (!confirmed) return;
    setPassingReleasedPlace(true);
    setError('');
    try {
      const passReleasedPlace = httpsCallable(functions, 'passReleasedLearnToRowPlace');
      await passReleasedPlace({
        teamId,
        courseId: selectedCourseId,
        targetUserId: effectiveTargetUserId,
      });
      setReleasedToPayment(false);
      setOutcome('waitlisted');
    } catch (err) {
      setError(err?.message || 'Could not return this Learn to Row place to the waitlist.');
    } finally {
      setPassingReleasedPlace(false);
    }
  }, [effectiveTargetUserId, releasedToPayment, selectedCourseId, teamId]);

  if (outcome === 'waitlisted') {
    return (
      <div className="text-center">
        <h3>You&apos;re on the Learn to Row waitlist</h3>
        <p className="text-muted">
          The club will release places from the shared Learn to Row waitlist as they become available.
        </p>
        {courses.length > 0 ? (
          <>
            <p className="text-muted">
              There are also open Learn to Row intakes available now on a first-come, first-served basis. Your waitlist place does not reserve a spot on those courses.
            </p>
            <button type="button" className="button button-secondary" onClick={() => setOutcome('')}>
              View open courses
            </button>
          </>
        ) : null}
      </div>
    );
  }

  if (outcome === 'awaitingApproval') {
    return (
      <div className="text-center">
        <h3>Learn to Row application received</h3>
        <p className="text-muted">
          Your join answers have been submitted. The club will review your Learn to Row application before adding you to the live waitlist.
        </p>
      </div>
    );
  }

  if (outcome === 'joined') {
    return (
      <div className="form-stacked space-y-4 text-left">
        {error ? <ErrorMessage message={error} /> : null}
        <div className="join-family-coverage">
          <h3 style={{ marginTop: 0 }}>Learn to Row place confirmed</h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            {requiresSessionRsvps
              ? 'Your course signup is active. Please RSVP Yes or No to each course date before heading into the club.'
              : 'Your course signup is active. The club has not asked for separate session RSVPs for this course.'}
          </p>
        </div>

        {effectiveJoinedSessions.length > 0 ? (
          <div className="join-family-coverage">
            <h3 style={{ marginTop: 0 }}>{selectedCourse?.name || 'Learn to Row'}</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {effectiveJoinedSessions.map((session, index) => {
                  return requiresSessionRsvps ? (
                    <LearnToRowSessionRsvpCard
                      key={session.id}
                      session={session}
                      sessionIndex={index}
                      selectedCourseId={selectedCourseId}
                      teamId={teamId}
                      targetUserId={effectiveTargetUserId}
                      onStatusChange={handleSessionStatusChange}
                    />
                  ) : (
                    <div key={session.id} className="card" style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>Session {index + 1}</div>
                      <div className="text-muted" style={{ marginTop: 4 }}>{formatSession(session)}</div>
                      <CourseBoatSummary teamId={teamId} courseType="learn_to_row" courseId={selectedCourseId} sessionId={session.id} />
                    </div>
                  );
                })}
            </div>
          </div>
        ) : null}

        <div className="form-actions">
          {effectiveTargetIsCurrentAccount ? (
            <button type="button" className="button" onClick={handleOpenClub} disabled={!allSessionsAnswered}>
              Open club
            </button>
          ) : (
            <>
              <button
                type="button"
                className="button"
                onClick={handleOpenTargetAccountClub}
                disabled={!allSessionsAnswered}
              >
                Go to family accounts
              </button>
              <p className="text-muted" style={{ margin: 0 }}>
                This place is attached to {targetAccountLabel}. Open the club from that account to see the course.
              </p>
            </>
          )}
          {!allSessionsAnswered ? (
            <p className="text-muted" style={{ margin: 0 }}>
              RSVP to every session to continue.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (checkoutState === 'success') {
    return (
      <div className="form-stacked space-y-4 text-left">
        {error ? <ErrorMessage message={error} /> : null}
        {checkoutActivationFailed ? (
          <div className="join-family-coverage">
            <h3 style={{ marginTop: 0 }}>Payment needs checking</h3>
            <p className="text-muted" style={{ marginBottom: 12 }}>
              Payment was received, but the Learn to Row place could not be activated automatically. Please contact the club so they can check the signup.
            </p>
            <button type="button" className="button button-secondary" onClick={handleRetryCheckoutConfirmation}>
              Check again
            </button>
          </div>
        ) : checkoutIntentFailed ? (
          <div className="join-family-coverage">
            <h3 style={{ marginTop: 0 }}>Payment not completed</h3>
            <p className="text-muted" style={{ marginBottom: 12 }}>
              The Learn to Row place was not confirmed. You can return to the course signup and try payment again.
            </p>
            <button type="button" className="button button-secondary" onClick={() => navigate(`/join/${teamId}?joinMode=learn-to-row&courseId=${encodeURIComponent(selectedCourseId || preselectedCourseId)}`)}>
              Return to signup
            </button>
          </div>
        ) : (
          <div className="join-family-coverage">
            <h3 style={{ marginTop: 0 }}>Payment received</h3>
            <p className="text-muted" style={{ marginBottom: 0 }}>
              Finalising your Learn to Row place. This can take a few moments after Stripe sends the payment confirmation.
            </p>
          </div>
        )}
        {checkoutConfirmationTimedOut ? (
          <div className="join-family-coverage">
            <h3 style={{ marginTop: 0 }}>Still confirming payment</h3>
            <p className="text-muted" style={{ marginBottom: 12 }}>
              Payment has returned from Stripe, but the course place has not confirmed in the app yet. Check again to retry the confirmation without leaving this page.
            </p>
            <button type="button" className="button button-secondary" onClick={handleRetryCheckoutConfirmation}>
              Check again
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="form-stacked space-y-4 text-left">
      {error ? <ErrorMessage message={error} /> : null}

      {coursePaymentSetupBlocked ? (
        <div className="join-family-coverage">
          <h3 style={{ marginTop: 0 }}>Learn to Row is not ready yet</h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            The club needs to finish Stripe setup before Learn to Row waitlist or course payments can open.
          </p>
        </div>
      ) : null}

      {releasedToPayment ? (
        <div className="join-family-coverage">
          <h3 style={{ marginTop: 0 }}>Learn to Row place ready</h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            A club admin has released a Learn to Row place for you. Check the course dates below before you pay. Your saved answers and document acceptances are locked in.
          </p>
        </div>
      ) : null}

      {checkoutState === 'cancel' ? (
        <p className="text-muted">
          Payment was cancelled. You are not attached to a course until payment completes.
        </p>
      ) : null}

      <div className="form-group">
        <label>Choose Learn to Row route</label>
        <select className="form-input" value={selectedCourseId} onChange={(event) => setSelectedCourseId(event.target.value)} disabled={releasedToPayment}>
          {teamWaitlistOpen ? <option value="">General Learn to Row waitlist</option> : <option value="">Select course</option>}
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name || 'Untitled course'}
            </option>
          ))}
        </select>
        {courses.length === 0 && !teamWaitlistOpen ? (
          <p className="text-muted" style={{ marginTop: 8 }}>
            No public Learn to Row courses are currently open for signup.
          </p>
        ) : null}
        {courses.length === 0 && teamWaitlistOpen ? (
          <p className="text-muted" style={{ marginTop: 8 }}>
            There are no public course dates yet, but you can join the club&apos;s Learn to Row waitlist now.
          </p>
        ) : null}
      </div>

      {selectedCourse ? (
        <div className="join-family-coverage">
          <h3 style={{ marginTop: 0 }}>{selectedCourse.name || 'Learn to Row'}</h3>
          {selectedCourse.description ? <p className="text-muted">{selectedCourse.description}</p> : null}
          <p>
            Fee: <strong>{formatMoney(selectedCourse.courseFeeMinor || 0, stripeSetup?.currency || 'GBP')}</strong>
          </p>
          <p className="text-muted">
            {Math.max(1, Number(selectedCourse.capacity || 1))} places available on this intake.
            {teamWaitlistOpen
              ? normalizeLearnToRowSignupMode(selectedCourse) === 'open_to_all'
                ? ' This intake is open publicly on a first-come, first-served basis. The generic Learn to Row waitlist does not reserve a place on it.'
                : ' If you do not want this intake, you can stay on the generic Learn to Row waitlist for a future release.'
              : ''}
          </p>
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            {normalizeSessions(selectedCourse.sessions || []).map((session, index) => {
              const parts = formatSessionParts(session);
              return (
                <div
                  key={session.id}
                  style={{
                    border: '1px solid #dbe4f0',
                    borderRadius: 12,
                    padding: '0.9rem 1rem',
                    background: '#f8fbff',
                  }}
                >
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#4b5563' }}>
                    Session {index + 1}
                  </div>
                  <div style={{ fontSize: '1.02rem', fontWeight: 700, marginTop: 4 }}>
                    {parts.dayLabel}
                  </div>
                  <div className="text-muted" style={{ marginTop: 2 }}>
                    {parts.dateLabel}
                  </div>
                  <div style={{ marginTop: 8, fontWeight: 600 }}>
                    {parts.timeLabel}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              marginTop: 14,
              border: courseAcknowledged ? '1px solid #bbf7d0' : '1px solid #fde68a',
              background: courseAcknowledged ? '#f0fdf4' : '#fffbeb',
              borderRadius: 12,
              padding: '0.95rem 1rem',
            }}
          >
            <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#4b5563' }}>
              Before you continue
            </div>
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              Check that these dates and times work for the selected account.
            </div>
            <p className="text-muted" style={{ marginTop: 6, marginBottom: 0 }}>
              Continuing will secure this intake for the selected account.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
              <button
                type="button"
                className={courseAcknowledged ? 'button button-secondary' : 'button'}
                onClick={() => setCourseAcknowledged((current) => !current)}
              >
                {courseAcknowledged ? 'Dates confirmed' : 'Confirm these dates work'}
              </button>
              <span
                className="text-muted"
                style={{
                  fontWeight: 600,
                  color: courseAcknowledged ? '#166534' : undefined,
                }}
              >
                {courseAcknowledged
                  ? 'Ready to continue to payment.'
                  : 'You need to confirm this before payment can continue.'}
              </span>
            </div>
          </div>
          {releasedToPayment ? (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                Don&apos;t want this intake? Pass on it and keep your place on the generic Learn to Row waitlist.
              </p>
              <button
                type="button"
                className="button button-secondary"
                onClick={handlePassReleasedPlace}
                disabled={submitting || passingReleasedPlace}
              >
                {passingReleasedPlace ? 'Passing…' : 'Pass on this course'}
              </button>
            </div>
          ) : null}
        </div>
      ) : teamWaitlistOpen ? (
        <div className="join-family-coverage">
          <h3 style={{ marginTop: 0 }}>Learn to Row waitlist</h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            Join the club&apos;s generic Learn to Row waitlist. They can then pull you onto a course once dates are ready or a place is released.
          </p>
          {learnToRowJoinPricing.indicativePriceMajor ? (
            <p style={{ marginTop: 10, marginBottom: 0 }}>
              Indicative course price: <strong>{formatMoney(Math.round(Number(learnToRowJoinPricing.indicativePriceMajor || 0) * 100), stripeSetup?.currency || 'GBP')}</strong>
            </p>
          ) : null}
          {learnToRowJoinPricing.indicativePriceNote ? (
            <p className="text-muted" style={{ marginBottom: 0 }}>
              {learnToRowJoinPricing.indicativePriceNote}
            </p>
          ) : null}
          <p className="text-muted" style={{ marginBottom: 0 }}>
            You&apos;ll complete the Learn to Row join questions first, then the club approves the application before you enter the live waitlist.
          </p>
        </div>
      ) : null}

      <div className="form-group join-family-coverage">
        <label>Who is this place for?</label>
        <p className="text-muted" style={{ marginTop: '0.25rem' }}>
          Current account: <strong>{activeAccountName}</strong>. Choose this account if the place should follow the profile you&apos;re using now, or pick someone else in your family.
        </p>
        <select className="form-input" value={targetMode} onChange={(event) => {
          const nextMode = event.target.value;
          setTargetMode(nextMode);
          if (nextMode === 'me') {
            setSelectedTargetUserId(user?.uid || '');
            setSelectedFamilyId('');
          } else if (nextMode === 'family') {
            setSelectedTargetUserId('');
            setSelectedFamilyId('');
          } else {
            setSelectedTargetUserId('');
            setSelectedFamilyId('');
          }
        }} required disabled={releasedToPayment}>
          <option value="">Select option</option>
          <option value="me" disabled={selfAlreadyInClub}>{activeAccountName} (current account)</option>
          <option value="family">Someone in my family</option>
        </select>
        {targetMode === 'me' ? (
          <p className="text-muted" style={{ marginTop: '0.5rem' }}>
            This Learn to Row place will be attached to {activeAccountSummaryLabel}.
          </p>
        ) : null}
        {selfAlreadyInClub ? (
          <p className="text-muted" style={{ marginTop: '0.5rem' }}>
            The current account is already in this club, so this Learn to Row link can only be used for someone else in your family.
          </p>
        ) : null}

        {familyMembershipGroups.length > 0 && targetMode === 'family' ? (
          <>
            <select className="form-input" style={{ marginTop: 8 }} value={selectedFamilyId} onChange={(event) => {
              setSelectedFamilyId(event.target.value);
              setSelectedTargetUserId('');
            }} required disabled={releasedToPayment}>
              <option value="">Select family</option>
              {familyMembershipGroups.map((group) => (
                <option key={group.familyId} value={group.familyId}>{group.familyName}</option>
              ))}
            </select>

            <select className="form-input" style={{ marginTop: 8 }} value={selectedTargetUserId} onChange={(event) => setSelectedTargetUserId(event.target.value)} disabled={releasedToPayment || !selectedFamilyId} required>
              <option value="">Select family account</option>
              {selectedFamilyMembers.map((member) => (
                <option key={member.uid} value={member.uid}>
                  {member.name || 'Family account'}{member.role ? ` - ${member.role}` : ''}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <p className="text-muted" style={{ marginTop: '0.5rem' }}>
          {familyMembershipGroups.length > 0
            ? 'Buying for a child? Choose someone in your family, then select their child account so the Learn to Row place is attached to them.'
            : 'Buying for a child or another family member? Set up family accounts first so you can choose them here.'}
        </p>
        {(familyMembershipGroups.length === 0 || (targetMode === 'family' && selectedFamilyId && selectedFamilyMembers.length === 0)) ? (
          <div style={{ marginTop: '0.5rem' }}>
            <p className="text-muted" style={{ marginBottom: '0.4rem' }}>
              Need to buy for a child? Add their child account first.
            </p>
            <button type="button" className="button button-secondary" onClick={() => navigate(buildChildAccountSetupPath(childAccountReturnPath))}>
              Set Up Child Account
            </button>
          </div>
        ) : null}
      </div>

      {selectedCourse ? (
        <div className="form-group join-family-coverage">
          <h3 style={{ marginTop: 0 }}>Payment total</h3>
          {effectiveCoursePricePreview ? (
            <div style={{ display: 'grid', gap: 4, marginTop: 10 }}>
              {effectiveCoursePricePreview.chargedPlatformFeeMinor > 0 ? (
                <div className="text-muted">
                  Member Nest fee added: {formatMoney(effectiveCoursePricePreview.chargedPlatformFeeMinor, stripeSetup?.currency || 'GBP')}
                </div>
              ) : null}
              {effectiveCoursePricePreview.chargedStripeFeeMinor > 0 ? (
                <div className="text-muted">
                  Card fee added: {formatMoney(effectiveCoursePricePreview.chargedStripeFeeMinor, stripeSetup?.currency || 'GBP')}
                </div>
              ) : null}
              {effectiveDiscountAppliedMinor > 0 ? (
                <div className="text-muted">
                  Discount applied: -{formatMoney(effectiveDiscountAppliedMinor, stripeSetup?.currency || 'GBP')}
                </div>
              ) : null}
              {effectiveStoredCreditAppliedMinor > 0 ? (
                <div className="text-muted">
                  Stored membership credit applied: -{formatMoney(effectiveStoredCreditAppliedMinor, stripeSetup?.currency || 'GBP')}
                </div>
              ) : null}
              <div style={{ fontWeight: 700 }}>
                Estimated total due today: {formatMoney(effectiveCoursePricePreview.totalDueMinor, stripeSetup?.currency || 'GBP')}
              </div>
            </div>
          ) : null}

          <label style={{ display: 'block', marginTop: 12 }}>
            Discount code
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch', flexWrap: 'wrap', marginTop: 6 }}>
              <input
                className="form-input"
                value={membershipDiscountCode}
                onChange={(event) => setMembershipDiscountCode(event.target.value.toUpperCase())}
                placeholder="Optional"
                disabled={submitting || applyingDiscount}
                style={{ flex: '1 1 180px', minWidth: 0 }}
              />
              <button
                type="button"
                className="button button-secondary"
                onClick={handleApplyDiscount}
                disabled={submitting || applyingDiscount || !membershipDiscountCode.trim() || !selectedTargetUserId}
                style={{ flex: '0 0 auto' }}
              >
                {applyingDiscount ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </label>
          {discountMessage ? (
            <p className="text-muted" style={{ marginTop: 6, marginBottom: 0 }}>
              {discountMessage}
            </p>
          ) : null}
          {activePricingPreview && effectiveStoredCreditRemainingMinor > 0 ? (
            <p className="text-muted" style={{ marginTop: 6, marginBottom: 0 }}>
              Stored membership credit remaining after today: {formatMoney(effectiveStoredCreditRemainingMinor, stripeSetup?.currency || 'GBP')}.
            </p>
          ) : null}
        </div>
      ) : null}

      {effectiveJoinQuestions.length > 0 && !releasedToPayment ? (
        <div className="form-group join-family-coverage">
          <label>Learn to Row questions</label>
          <p className="text-muted" style={{ marginTop: '0.25rem' }}>
            {learnToRowJoinMode === 'club_join_form'
              ? 'These are synced from the club join form.'
              : learnToRowJoinMode === 'club_join_form_plus_l2r'
                ? 'These combine the club join form with Learn to Row-specific questions.'
                : 'These are Learn to Row-specific join questions.'}
          </p>
          <p className="text-muted" style={{ marginTop: '0.25rem' }}>
            Saved Learn to Row answers are reused if you come back later to pay for a released place, and synced answers won&apos;t need re-entering when you convert to membership.
          </p>

          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
            {effectiveJoinQuestions.map((question) => (
              <div key={question.id} className="form-group" style={{ marginBottom: 0 }}>
                {question.type === 'checkbox' ? (
                  renderJoinQuestionInput(question)
                ) : (
                  <>
                    <label>
                      {question.label}
                      {question.required ? <span style={{ color: 'red' }}> *</span> : null}
                    </label>
                    {renderJoinQuestionInput(question)}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0 && !releasedToPayment ? (
        <div className="form-group join-family-coverage">
          <label>Required Club Documents</label>
          <p className="text-muted" style={{ marginTop: '0.25rem' }}>
            Review and accept each required document before your Learn to Row application can be submitted.
          </p>

          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
            {requiredJoinDocuments.map((document) => {
              const sourceUrl = getDocumentSourceUrl(document);
              const actionLabels = getDocumentActionLabels(document);
              const checkboxLabel = document.acceptanceLabel || `I accept ${document.title || 'this document'}.`;
              return (
                <div key={document.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.85rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                      <strong>{document.title || 'Required document'}</strong>
                      {document.description ? (
                        <p className="text-muted" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                          {document.description}
                        </p>
                      ) : null}
                    </div>
                    {sourceUrl ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <a className="button button-secondary" href={sourceUrl} target="_blank" rel="noreferrer">
                          {actionLabels.view}
                        </a>
                        <button type="button" className="button button-secondary" onClick={() => triggerDocumentDownload(document)}>
                          {actionLabels.download}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={acceptedDocuments?.[document.id] === true}
                      onChange={(event) => setAcceptedDocuments((current) => ({
                        ...current,
                        [document.id]: event.target.checked,
                      }))}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span>{checkboxLabel}</span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" className="button button-secondary" onClick={() => navigate(`/join/${teamId}`)}>
          Back to membership join
        </button>
        <SubmitButton
          label={submitting ? 'Working…' : releasedToPayment ? 'Continue to payment' : (selectedCourseId ? 'Continue to payment / waitlist' : 'Join waitlist')}
          variant="primary"
          onClick={() => handleStart()}
          disabled={coursePaymentSetupBlocked || submitting || passingReleasedPlace || (!selectedCourseId && !teamWaitlistOpen) || !targetMode || !selectedTargetUserId || (requiresCourseAcknowledgement && !courseAcknowledged)}
        />
      </div>
    </div>
  );
}
